/**
 * Utility Functions for Theme Features
 * Provides helper functions for managing theme options, hashing strings, escaping RegExp,
 * and manipulating HTML content.
 */

// Attach exported functions to window.bds_theme.utils for global access
if (typeof window !== "undefined") {
	window.bds_theme = window.bds_theme || {};
	window.bds_theme.utils = {
		hashString,
		escapeRegExp,
		splitHtml,
		setCookie,
		getCookie,
	};
}

/**
 * Simple hash function for strings (djb2 algorithm).
 * @param {string} str - The input string to hash.
 * @returns {number} The resulting hash as an unsigned integer.
 */
export function hashString(str) {
	let hash = 5381;

	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) + hash + str.charCodeAt(i); // hash * 33 + c
	}

	return hash >>> 0; // Ensure the hash is unsigned
}

/**
 * Escape special characters in a string for use in a RegExp pattern.
 * @param {string} string - The string to escape.
 * @returns {string} The escaped string, safe for RegExp usage.
 */
export function escapeRegExp(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Split an HTML string into chunks of text and tags, similar to wp_html_split.
 * @param {string} html - The HTML string to split.
 * @returns {string[]} An array of text and HTML tag segments.
 */
export function splitHtml(html) {
	// Regex to match HTML tags and comments
	const regex = /(<[^>]+>|<!--[\s\S]*?-->)/g;
	return html.split(regex);
}

/**
 * Set a cookie with a specified name, value, and duration.
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {number} duration - The duration for which the cookie should be valid.
 * @param {string} [durationType='days'] - The type of duration ('days', 'months', or 'milliseconds').
 */
export function setCookie(name, value, duration, durationType = "days") {
	const date = new Date();

	if (durationType === "months") {
		date.setMonth(date.getMonth() + duration);
	} else if (durationType === "days") {
		date.setTime(date.getTime() + duration * 24 * 60 * 60 * 1000);
	} else {
		date.setTime(date.getTime() + duration); // treat as milliseconds
	}

	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * Get the value of a cookie by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie, or null if not found.
 *
 */
export function getCookie(name) {
	const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));

	return match ? decodeURIComponent(match[2]) : null;
}
